import * as Sentry from '@sentry/react';

interface UserContext {
  id?: string;
  email?: string;
  name?: string;
}

/**
 * Initialize Sentry for error tracking and performance monitoring.
 * Sets up browser tracing, session replay, and error filtering.
 *
 * Configuration includes:
 * - Environment-specific trace sampling rates
 * - Session replay with configurable sampling
 * - Error filtering for development noise
 * - Conditional enabling based on environment
 *
 */
export function initSentry() {
  const isProduction = import.meta.env.PROD;
  const sentryEnabled = import.meta.env.VITE_SENTRY_ENABLED === 'true';
  const sentryDsn = import.meta.env.VITE_SENTRY_DSN;

  // Use the actual deployment environment, not just production/development
  const environment =
    import.meta.env.VITE_ENVIRONMENT ||
    (isProduction ? 'production' : 'development');

  Sentry.init({
    dsn: sentryDsn,
    environment,
    tracesSampleRate: isProduction ? 0.1 : 1.0,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    // Capture replay sessions on errors only in production
    replaysSessionSampleRate: isProduction ? 0 : 0.1,
    replaysOnErrorSampleRate: 1.0,

    // Don't report errors in development unless explicitly enabled
    enabled: isProduction || sentryEnabled,

    beforeSend(event) {
      // Filter out known non-critical errors in development
      if (!import.meta.env.PROD) {
        const message =
          event.exception?.values?.[0]?.value || event.message || '';

        // Filter out common development noise
        if (
          message.includes('ChunkLoadError') ||
          message.includes('Loading chunk') ||
          message.includes('ResizeObserver loop limit exceeded')
        ) {
          return null;
        }
      }

      return event;
    },
  });
}

/**
 * Set user context in Sentry for error attribution.
 * Associates all subsequent errors and events with the provided user information.
 *
 * @param user - User context information
 * @param user.id - Unique user identifier
 * @param user.email - User's email address
 * @param user.name - User's display name
 *
 * @example
 * ```typescript
 * // Set user context after successful login
 * setSentryUserContext({
 *   id: 'user123',
 *   email: 'user@example.com',
 *   name: 'John Doe'
 * });
 * ```
 */
export function setSentryUserContext(user: UserContext) {
  Sentry.setUser({
    id: user.id,
    email: user.email,
    username: user.name,
  });
}

/**
 * Clear user context from Sentry.
 * Removes user association from subsequent errors and events.
 * Typically called during logout to ensure user privacy.
 *
 */
export function clearSentryUserContext() {
  Sentry.setUser(null);
}

/**
 * Add additional context to the current Sentry scope.
 * Context data is attached to all subsequent events and errors.
 *
 * @param key - Context namespace key
 * @param context - Context data object with arbitrary key-value pairs
 *
 * @example
 * ```typescript
 * // Add business context to errors
 * setSentryContext('order', {
 *   orderId: 'ORD-123',
 *   customerId: 'CUST-456',
 *   totalAmount: 99.99
 * });
 * ```
 */
export function setSentryContext(
  key: string,
  context: Record<string, unknown>
) {
  Sentry.setContext(key, context);
}

/**
 * Add tags to the current Sentry scope.
 * Tags are searchable key-value pairs for filtering and grouping events.
 *
 * @param tags - Record of tag key-value pairs (values must be strings)
 *
 * @example
 * ```typescript
 * // Tag errors by feature and user type
 * setSentryTags({
 *   feature: 'contract-search',
 *   userType: 'lender',
 *   version: '1.2.3'
 * });
 * ```
 */
export function setSentryTags(tags: Record<string, string>) {
  Sentry.setTags(tags);
}

/**
 * Set the current route in Sentry scope for better error context.
 * Records the active route and optional search parameters for debugging.
 *
 * @param route - Current route path (e.g., '/contracts/search')
 * @param searchParams - Optional search/query parameters as key-value pairs
 *
 * @example
 * ```typescript
 * // Track route with search parameters
 * setSentryRoute('/contracts/search', {
 *   page: '1',
 *   limit: '25',
 *   query: 'toyota'
 * });
 * ```
 */
export function setSentryRoute(
  route: string,
  searchParams?: Record<string, string>
) {
  Sentry.setTag('route', route);

  if (searchParams && Object.keys(searchParams).length > 0) {
    Sentry.setContext('search_params', searchParams);
  }
}

/**
 * Report an error to Sentry with optional context and severity level.
 * Creates a new scope to isolate context from other events.
 *
 * @param error - Error object, string message, or any value to report
 * @param context - Optional additional context data
 * @param level - Severity level (default: 'error')
 *
 * @example
 * ```typescript
 * // Report API error with context
 * reportErrorToSentry(
 *   new Error('API request failed'),
 *   {
 *     endpoint: '/api/contracts',
 *     statusCode: 500,
 *     userId: 'user123'
 *   },
 *   'fatal'
 * );
 * ```
 */
export function reportErrorToSentry(
  error: unknown,
  context?: Record<string, unknown>,
  level: Sentry.SeverityLevel = 'error'
) {
  Sentry.withScope((scope) => {
    scope.setLevel(level);

    if (context) {
      Object.entries(context).forEach(([key, value]) => {
        if (typeof value === 'object' && value !== null) {
          scope.setContext(key, value as Sentry.Context);
        } else {
          scope.setTag(key, String(value));
        }
      });
    }

    if (error instanceof Error) {
      Sentry.captureException(error);
    } else {
      Sentry.captureMessage(String(error), level);
    }
  });
}

/**
 * Map HTTP status codes to appropriate Sentry severity levels.
 * Provides consistent severity classification for API errors.
 *
 * @param statusCode - HTTP status code (optional)
 * @returns Corresponding Sentry severity level
 *
 * @example
 * ```typescript
 * const severity = getStatusCodeSeverity(404); // returns 'error'
 * const severity = getStatusCodeSeverity(500); // returns 'fatal'
 * const severity = getStatusCodeSeverity(200); // returns 'info'
 * ```
 */
export function getStatusCodeSeverity(
  statusCode?: number
): Sentry.SeverityLevel {
  if (!statusCode) return 'error';

  if (statusCode >= 500) return 'fatal';
  if (statusCode === 401) return 'warning';
  if (statusCode >= 400) return 'error';
  if (statusCode >= 300) return 'warning';
  return 'info';
}

/**
 * Represents a search parameter value that can be converted to a string
 */
type SearchParamValue =
  | string
  | number
  | boolean
  | undefined
  | null
  | Array<string | number | boolean>;

/**
 * Represents a search parameters object from TanStack Router
 */
type SearchParams = Record<string, SearchParamValue>;

/**
 * Convert TanStack Router search params to a string record for Sentry context
 * Handles arrays by joining them with commas, converts other values to strings
 *
 * @param search - Search parameters from TanStack Router's useSearch hook or unknown object
 * @returns A record of string key-value pairs suitable for Sentry context
 *
 * @example
 * ```typescript
 * const search = useSearch({ strict: false });
 * const sentryParams = convertSearchParamsForSentry(search);
 * // { page: "1", categories: "electronics,gifts", sort: "price" }
 * ```
 */
export function convertSearchParamsForSentry(
  search: SearchParams
): Record<string, string> {
  const searchParams: Record<string, string> = {};

  // Handle both typed search params and unknown objects for flexibility
  if (search && typeof search === 'object' && !Array.isArray(search)) {
    Object.entries(search).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          // Convert arrays to comma-separated strings, filtering out null/undefined
          const validValues = value.filter(
            (v) => v !== undefined && v !== null
          );
          searchParams[key] = validValues.map((v) => String(v)).join(',');
        } else {
          searchParams[key] = String(value);
        }
      }
    });
  }

  return searchParams;
}

/**
 * Create an error boundary component with Sentry integration
 */
export const SentryErrorBoundary = Sentry.withErrorBoundary;
